import React, { useState } from "react";
import BaseButton from "../../../components/ui/BaseButton";
import BaseModal from "../../../components/ui/BaseModal";
import { useDispatch, useSelector } from "react-redux";
import "./style.css";
import {
  acceptRoutedOffer,
  assignWorkOrderToFreelancer,
  createOffer,
  declineOffer,
} from "../../../services/api/offers";
import { showToast } from "../../../store/toast/toastSlice";
import { useMutation, useQuery } from "@tanstack/react-query";
import useAuth from "../../../hooks/useAuth";
import QualificationMatcher from "../components/QualificationMatcher";
import BaseCard from "../../../components/ui/BaseCard";
import ScheduleCard from "../ScheduleCard";
import LocationCard from "../LocationCard";
import RateCard from "../RateCard";
import LocationMap from "../TabsCard/LocationMap";
import { useUpdateWorkOrder } from "../../../hooks/useUpdateWorkOrder";
import { useNavigate } from "react-router-dom";
import { getFreelanceCompanyMembers } from "../../../services/api/freelanceCompany";
import BaseAutoSelect from "../../../components/ui/BaseAutoSelect";
import { Avatar, MenuItem } from "@mui/material";

const RequestOfferModal = ({
  requestOfferModalOpen,
  setRequestOfferModalOpen,
  refetchUserAllowedAction,
  userAllowedAction,
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { auth } = useAuth();
  const { updateWorkOrder } = useUpdateWorkOrder();
  const workOrderDetail = useSelector((state) => state.workOrder.workOrderDetails);

  const isFreelanceCompanyOwner =
    auth?.user?.role?.role === "FREELANCER" && auth?.user?.company?.ownerId === auth?.user?.id;
  const [selectedEngineerId, setSelectedEngineerId] = useState(null);
  const [isAllQualificationsPassed, setIsAllQualificationsPassed] = useState(false);

  const { data: membersData } = useQuery({
    queryKey: ["freelanceCompanyMembers", workOrderDetail?.id],
    queryFn: () =>
      getFreelanceCompanyMembers({
        limit: 100,
        approvedOnly: true,
        workOrderId: workOrderDetail?.id,
      }),
    enabled: !!isFreelanceCompanyOwner,
  });

  const memberOptions = (membersData?.data?.data || []).map((m) => ({
    label: `${m.user.firstName} ${m.user.lastName}`,
    value: m.user.id,
    userId: m.user.id,
    profilePicture: m.user.profilePicture || null,
  }));

  const {
    isPending: isCreateOfferLoading,
    status,
    mutateAsync: createOfferMutation,
  } = useMutation({
    mutationFn: createOffer,
    onSuccess: (data) => {
      dispatch(
        showToast({
          message: "Offer Sent Successfully",
          type: "success",
        }),
      );
    },
  });
  const {
    isPending: isAcceptRoutedOfferLoading,
    status: offerStatus,
    mutateAsync: acceptRoutedOfferMutation,
  } = useMutation({
    mutationFn: acceptRoutedOffer,
    onSuccess: (data) => {
      dispatch(
        showToast({
          message: "Offer Accepted Successfully",
          type: "success",
        }),
      );
    },
  });

  const {
    isPending: isAssignLoading,
    mutateAsync: assignWorkOrderToFreelancerMutation,
  } = useMutation({
    mutationFn: assignWorkOrderToFreelancer,
    onSuccess: (data) => {
      dispatch(
        showToast({
          message: "Job Assign Successfully",
          type: "success",
        }),
      );
    },
    onError: (error) => {
      dispatch(
        showToast({
          message: error?.response?.data?.message,
          type: "error",
        }),
      );
    },
  });
  const {
    // status,
    mutateAsync: declineOfferMutation,
  } = useMutation({
    mutationFn: declineOffer,
    onSuccess: (data) => {
      dispatch(
        showToast({
          message: "Job Updated Successfully",
          type: "success",
        }),
      );
    },
  });

  async function assignWorkOrder() {
    try {
      let payload = {
        userId: auth.user.id,
        workOrderId: workOrderDetail.id,
      };
      await assignWorkOrderToFreelancerMutation(payload);
    } catch (error) {
      // Error toast is handled by this mutation's onError above.
    }
  }

  const handleSubmitButton = async () => {
    if (isFreelanceCompanyOwner && !selectedEngineerId) {
      dispatch(showToast({ message: "Please select an engineer", type: "error" }));
      return;
    }
    if (!isAllQualificationsPassed) {
      dispatch(
        showToast({
          message: "Please add qualification to profile",
          type: "error",
        }),
      );

      return;
    }
    try {
      if (userAllowedAction.isRouted) {
        await acceptRoutedOfferMutation({
          offerId: userAllowedAction?.routedOfferId,
          referredEngineerId: isFreelanceCompanyOwner ? selectedEngineerId : undefined,
        });
      } else {
        await createOfferMutation({
          workOrderId: workOrderDetail?.id,
          userId: auth?.user.id,
          ...(isFreelanceCompanyOwner && selectedEngineerId
            ? { referredEngineerId: selectedEngineerId }
            : {}),
        });
      }

      updateWorkOrder();
      refetchUserAllowedAction();
      setRequestOfferModalOpen(false);
    } catch (error) {
      dispatch(
        showToast({
          message: error?.response?.data?.message || "Something went wrong",
          type: "error",
        }),
      );
    }
  };
  async function handleNotInterestedButton() {
    try {
      let payload = {
        workOrderId: workOrderDetail?.id,
        status: "DECLINED",
        ...(userAllowedAction?.routedOfferId && { offerId: userAllowedAction.routedOfferId }),
      };
      await declineOfferMutation(payload);
      await updateWorkOrder();
      await refetchUserAllowedAction();
      setRequestOfferModalOpen(false);
      navigate("/jobs");
    } catch (error) {
      dispatch(
        showToast({
          message: error?.response?.data?.message || "Something went wrong",
          type: "error",
        }),
      );
    }
  }

  console.log("🚀 ~ RequestOfferModal ~ userAllowedAction:", userAllowedAction);
  return (
    <BaseModal open={requestOfferModalOpen} close={() => setRequestOfferModalOpen(false)}>
      <div className="container bg-[#F6F6F6]">
        <h6
          className="text-dark border-bottom"
          style={{ marginBottom: "24px", paddingBottom: "4px" }}
        >
          {userAllowedAction.isRouted ? "Accept Work Order" : "Request Work Order"}
          {userAllowedAction.isWorkOrderAlreadyAssigned && (
            <span className="text-red-700 mt-2 text-sm ml-2">
              (This workorder has been accepted by another engineer.)
            </span>
          )}
        </h6>
        {isFreelanceCompanyOwner && (
          <BaseCard className="p-4 mb-4" elevation={0}>
            <h6 className="text-dark mb-3">Select Engineer</h6>
            <BaseAutoSelect
              isDefaultField={false}
              label="Engineer"
              labelShrink={false}
              required={true}
              options={memberOptions}
              value={selectedEngineerId || ""}
              onChange={(e) => {
                setSelectedEngineerId(e.target.value);
                setIsAllQualificationsPassed(false);
              }}
              renderOption={(props, option) => {
                const { key, ...optionProps } = props;
                return (
                  <MenuItem
                    key={option.value}
                    value={option.value}
                    {...optionProps}
                    style={{
                      padding: "10px 16px",
                      display: "flex",
                      alignItems: "center",
                      gap: "12px",
                    }}
                  >
                    <Avatar
                      src={option.profilePicture}
                      sx={{ width: 36, height: 36, fontSize: "14px" }}
                    >
                      {option.label?.charAt(0)}
                    </Avatar>
                    <div>
                      <p style={{ margin: 0, fontSize: "13px", fontWeight: 400, color: "#686868" }}>
                        {option.label}
                      </p>
                      <p style={{ margin: 0, fontSize: "11px", color: "#9e9e9e" }}>
                        ID: {option.userId}
                      </p>
                    </div>
                  </MenuItem>
                );
              }}
              ListboxProps={{ style: { maxHeight: 320 } }}
            />
          </BaseCard>
        )}
        {(!isFreelanceCompanyOwner || selectedEngineerId) && !isAllQualificationsPassed && (
          <BaseCard className="p-4" elevation={0}>
            <h6 className="text-dark mb-3">
              WO Qualifications
              {!isAllQualificationsPassed && (
                <>
                  <span>: </span>
                  <span className="text-xs text-red-600">
                    (To proceed with this work order request, please ensure you add the required
                    qualifications)
                  </span>
                </>
              )}
            </h6>
            <QualificationMatcher
              setIsAllQualificationsPassed={setIsAllQualificationsPassed}
              workOrderQualifications={workOrderDetail?.qualificationSubTypes}
              overrideUserId={isFreelanceCompanyOwner ? selectedEngineerId : undefined}
            />
          </BaseCard>
        )}

        <div className="grid grid-cols-3 2xl:gap-12 gap-4  mt-6">
          <div className="xl:col-span-1 col-span-3">
            <RateCard />
          </div>
          <div className="xl:col-span-1 col-span-3">
            <ScheduleCard
              timeLogs={workOrderDetail?.timeLogs}
              tasks={workOrderDetail?.tasks}
              status={workOrderDetail?.status}
              assignedTo={workOrderDetail?.assignedTo}
              schedule={{
                timeZone: workOrderDetail?.address?.timeZone,
                scheduleType: workOrderDetail?.scheduleType,
                orderStartDate: workOrderDetail?.orderStartDate,
                orderStartTime: workOrderDetail?.orderStartTime,
                orderEndDate: workOrderDetail?.orderEndDate,
                orderEndTime: workOrderDetail?.orderEndTime,
                confirmedWorkOrderAt: workOrderDetail?.tasks?.find(
                  (task) => task.taskType === "START_TIME",
                )?.completedTask?.text,
              }}
            />
          </div>

          <div className="xl:col-span-1 col-span-3">
            <LocationCard location={workOrderDetail?.address} />
          </div>
        </div>

        <div className="mt-6 hidden lg:block">
          <LocationMap location={workOrderDetail?.address} />
        </div>
      </div>
      <div className="xl:flex grid grid-cols-2 gap-4 xl:justify-center  bg-white py-6 px-4">
        <div className="xl:block col-span-2">
          <BaseButton
            className="py-[6px] px-[40px] bg-light text-dark"
            fullWidth={true}
            onClick={() => setRequestOfferModalOpen(false)}
          >
            Cancel
          </BaseButton>
        </div>
        <div className="xl:block col-span-2">
          <BaseButton
            loaderSize={24}
            loaderColor="primary"
            disabled={!isAllQualificationsPassed}
            isLoading={isCreateOfferLoading || isAssignLoading}
            className="py-[6px] px-[40px]"
            color="primary"
            fullWidth={true}
            onClick={
              userAllowedAction.isWorkOrderAlreadyAssigned
                ? handleNotInterestedButton
                : handleSubmitButton
            }
          >
            {userAllowedAction.isWorkOrderAlreadyAssigned
              ? "Remove"
              : userAllowedAction.isRouted
                ? "Accept"
                : "Send a Request"}
          </BaseButton>
        </div>
      </div>
    </BaseModal>
  );
};

export default RequestOfferModal;
